import * as React from 'react';
import { useState } from 'react';
import { Box, Divider } from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import {
  useTranslate,
  DashboardMenuItem,
  MenuItemLink,
  MenuProps,
  useSidebarState,
} from 'react-admin';
import clsx from 'clsx';

import SubMenu from './SubMenu';

import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import CategoryIcon from '@mui/icons-material/Category';
import ClassIcon from '@mui/icons-material/Class';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import InventoryIcon from '@mui/icons-material/Inventory';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PeopleIcon from '@mui/icons-material/People';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import ListAltIcon from '@mui/icons-material/ListAlt';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MapIcon from '@mui/icons-material/Map';

type MenuName =
  | 'menuCatalog'
  | 'menuSales'
  | 'menuCustomers'
  | 'menuBot'
  | 'menuModules'
  | 'menuSettings'
  | 'menuManager';

const Menu = ({ dense = false }: MenuProps) => {
  const [state, setState] = useState({
    menuCatalog: true,
    menuSales: true,
    menuCustomers: true,
    menuBot: true,
    menuModules: true,
    menuSettings: true,
    menuManager: true,
  });
  const translate = useTranslate();
  const [open] = useSidebarState();

  const handleToggle = (menu: MenuName) => {
    setState((state) => ({ ...state, [menu]: !state[menu] }));
  };

  return (
    <Box
      sx={{
        width: open ? 200 : 50,
        marginTop: 1,
        marginBottom: 1,
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
      className={clsx({
        'RaMenu-open': open,
        'RaMenu-closed': !open,
      })}
    >
      <DashboardMenuItem primaryText="Головна" />
      <SubMenu
        handleToggle={() => handleToggle('menuBot')}
        isOpen={state.menuBot}
        name="Бот"
        icon={<SmartToyIcon />}
        dense={dense}
      >
        <MenuItemLink
          to="/content"
          state={{ _scrollToTop: true }}
          primaryText={`Розділи`}
          leftIcon={<SmartToyIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/viber-settings"
          state={{ _scrollToTop: true }}
          primaryText={`Viber`}
          leftIcon={<PrecisionManufacturingIcon />}
          dense={dense}
        />
      </SubMenu>
      <SubMenu
        handleToggle={() => handleToggle('menuSales')}
        isOpen={state.menuSales}
        name="Модулі"
        icon={<WysiwygIcon />}
        dense={dense}
      >
        <MenuItemLink
          to="/modules-products"
          state={{ _scrollToTop: true }}
          primaryText={`Продукція`}
          leftIcon={<BackupTableIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/modules-orders"
          state={{ _scrollToTop: true }}
          primaryText={`Замовлення`}
          leftIcon={<ListAltIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/modules-history"
          state={{ _scrollToTop: true }}
          primaryText={`Історія`}
          leftIcon={<WorkHistoryIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/modules-macros"
          state={{ _scrollToTop: true }}
          primaryText={`Макроси`}
          leftIcon={<CodeOffIcon />}
          dense={dense}
        />
      </SubMenu>
      <SubMenu
        handleToggle={() => handleToggle('menuCatalog')}
        isOpen={state.menuCatalog}
        name="Товари"
        icon={<LocalGroceryStoreIcon />}
        dense={dense}
      >
        <MenuItemLink
          to="/categories"
          state={{ _scrollToTop: true }}
          primaryText={'Категорії'}
          leftIcon={<CategoryIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/sub-categories"
          state={{ _scrollToTop: true }}
          primaryText={'Підкатегорії'}
          leftIcon={<ClassIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/carousele"
          state={{ _scrollToTop: true }}
          primaryText={'Каталог'}
          leftIcon={<ViewCarouselIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/structure"
          state={{ _scrollToTop: true }}
          primaryText={'Структура'}
          leftIcon={<InventoryIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/products"
          state={{ _scrollToTop: true }}
          primaryText={'Товари'}
          leftIcon={<LocalGroceryStoreIcon />}
          dense={dense}
        />
      </SubMenu>
      <SubMenu
        handleToggle={() => handleToggle('menuManager')}
        isOpen={state.menuManager}
        name="Менеджмент"
        icon={<ManageAccountsIcon />}
        dense={dense}
      >
        <MenuItemLink
          to="/managers"
          state={{ _scrollToTop: true }}
          primaryText={'Менеджери'}
          leftIcon={<PeopleIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/shops"
          state={{ _scrollToTop: true }}
          primaryText={'Магазини'}
          leftIcon={<ShoppingBasketIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/cities"
          state={{ _scrollToTop: true }}
          primaryText={'Міста'}
          leftIcon={<MapIcon />}
          dense={dense}
        />
      </SubMenu>
      <SubMenu
        handleToggle={() => handleToggle('menuSettings')}
        isOpen={state.menuSettings}
        name="Інше"
        icon={<AttachEmailIcon />}
        dense={dense}
      >
        <MenuItemLink
          to="/broadcasts"
          state={{ _scrollToTop: true }}
          primaryText={`Розсилки`}
          leftIcon={<AttachEmailIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/media"
          state={{ _scrollToTop: true }}
          primaryText={`Медіа`}
          leftIcon={<PermMediaIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/settings"
          state={{ _scrollToTop: true }}
          primaryText={`Налаштування`}
          leftIcon={<TuneIcon />}
          dense={dense}
        />
        <Divider />
        <MenuItemLink
          to="/history"
          state={{ _scrollToTop: true }}
          primaryText={`Історія`}
          leftIcon={<HistoryIcon />}
          dense={dense}
        />
        <MenuItemLink
          to="/instructions"
          state={{ _scrollToTop: true }}
          primaryText={`Інструкція`}
          leftIcon={<MenuBookIcon />}
          dense={dense}
        />
      </SubMenu>
    </Box>
  );
};

export default Menu;
